import React, { useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { usePerformance } from '../hooks/usePerformance';
import { SynonymRotator } from './TextEffects';

const Intro = () => {
    const ref = useRef(null);
    const { isMobile, prefersReducedMotion, isLowEndDevice } = usePerformance();

    // Reduce number of animated orbs on mobile
    const orbCount = useMemo(() => {
        if (prefersReducedMotion) return 0;
        if (isMobile || isLowEndDevice) return 3; // Reduced from 7
        return 5; // Reduced from 7 on desktop
    }, [isMobile, prefersReducedMotion, isLowEndDevice]);

    const blurAmount = isMobile || isLowEndDevice ? '40px' : '75px';

    return (
        <section 
            ref={ref}
            className="w-full min-h-screen flex items-center bg-white/50 dark:bg-black/50 backdrop-blur-md relative overflow-hidden py-20" 
            id='about' 
            itemScope 
            itemType="https://schema.org/AboutPage"
        >
            {/* Vibrant animated background - full width */}
            {!prefersReducedMotion && (
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    {/* Dynamic gradient waves */}
                    {Array.from({ length: orbCount }).map((_, i) => (
                        <motion.div
                            key={i}
                            className="absolute rounded-full"
                            style={{
                                width: `${220 + i * 80}px`,
                                height: `${220 + i * 80}px`,
                                background: `radial-gradient(circle, ${
                                    i % 3 === 0 
                                        ? 'rgba(139, 92, 246, 0.65)' 
                                        : i % 3 === 1
                                        ? 'rgba(59, 130, 246, 0.65)'
                                        : 'rgba(236, 72, 153, 0.65)'
                                } 0%, transparent 70%)`,
                                filter: `blur(${blurAmount})`,
                                left: `${-5 + i * 15}%`,
                                top: `${10 + i * 12}%`,
                                opacity: 0.5,
                                willChange: 'transform, opacity',
                                contain: 'layout style paint',
                                transform: 'translateZ(0)',
                            }}
                            animate={{
                                x: [0, 50, -45, 0],
                                y: [0, -50, 45, 0],
                                scale: isMobile ? [1, 1.2, 0.85, 1] : [1, 1.3, 0.75, 1],
                                opacity: [0.5, 0.7, 0.35, 0.5],
                            }}
                            transition={{
                                duration: 13 + i * 1.8,
                                repeat: Infinity,
                                ease: "easeInOut",
                                delay: i * 1.2,
                                type: "tween",
                            }}
                        />
                    ))}
                </div>
            )}
            <div 
                className="flex flex-col justify-center text-center lg:text-left w-full relative z-10 max-w-7xl mx-auto px-2 md:px-12"
            >
                <div 
                    className="w-full max-w-4xl mx-auto lg:mx-0"
                >
                    <h2 
                        className="text-4xl md:text-6xl font-black mb-8 leading-tight"
                    >
                        <span className="text-gray-900 dark:text-white">Quand le monde </span>
                        <span className="text-gray-900 dark:text-white font-black">numérique</span>
                        <span className="text-gray-900 dark:text-white"> s'intègre au monde </span>
                        <span className="text-gray-900 dark:text-white font-black">physique</span>
                    </h2>
                    <div 
                        className="space-y-6 mb-10"
                    >
                        <p 
                            className='text-lg md:text-xl font-light leading-relaxed'
                        >
                            <span className="text-gray-600 dark:text-gray-300">Nous assistons à une convergence entre le monde physique et le monde numérique. Notre objectif est de catalyser </span>
                            <SynonymRotator 
                                synonyms={[
                                    'un changement radical', 
                                    'une transformation profonde', 
                                    'une révolution',
                                    'une évolution fulgurante',
                                    'une réinvention',
                                ]}
                                className="text-lg md:text-xl font-semibold text-gray-600 dark:text-gray-300"
                                interval={2000}
                                typingSpeed={50}
                            />
                            <span className="text-gray-600 dark:text-gray-300"> en créant des expériences nouvelles qui fusionnent ces deux mondes.</span>
                        </p>
                        <p 
                            className='text-lg md:text-xl font-light leading-relaxed'
                        >
                            <span className="text-gray-600 dark:text-gray-300">Chez </span>
                            <span className="text-gray-600 dark:text-gray-300 font-semibold">Tomorrow Global</span>
                            <span className="text-gray-600 dark:text-gray-300">, nous accompagnons les entreprises visionnaires qui comprennent l'importance cruciale de cette évolution, en leur apportant notre expertise pour naviguer dans cette nouvelle ère.</span>
                        </p>
                    </div>
                    <div
                    >
                        <Link 
                            to="/contact" 
                            className="text-white bg-gray-900 dark:bg-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100 inline-flex items-center justify-center px-8 py-4 text-lg font-semibold tracking-wide transition-all duration-300 group" 
                            aria-label="Contactez Tomorrow Global"
                        >
                        Contactez-nous
                            <motion.svg 
                                className="w-5 h-5 ml-2" 
                                xmlns="http://www.w3.org/2000/svg" 
                                viewBox="0 0 20 20" 
                                fill="currentColor" 
                                aria-hidden="true"
                                animate={{ x: [0, 5, 0] }}
                                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                            >
                                <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd"></path>
                            </motion.svg>
                    </Link>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default Intro;